import { ShieldCheck, TrendingUp } from "lucide-react";

/**
 * CompareChipBar — small horizontal strip of trust/trajectory badges
 * shown under each degree column header on the Compare page. Two
 * badges today:
 *   1. "UCAS Verified" — surfaces `ucas_course_code_live_verified`
 *      so families can tell at a glance which side of the compare
 *      is confirmed against UCAS' own live search index vs. an
 *      unverified estimate.
 *   2. "LEO 3y→5y" trajectory — reuses HESA `leo3_median_gbp` /
 *      `leo5_median_gbp` to show salary progression as a compact
 *      "£38k → £62k · +63%" chip.
 *
 * Kept intentionally lightweight; renders `null` when a degree has
 * neither signal so the table column doesn't gain extra vertical
 * space for no reason.
 */
export default function CompareChipBar({ degree, testidBase }) {
  if (!degree) return null;
  const hs = degree.hesa_stats || {};
  const leo3 = hs.leo3_median_gbp;
  const leo5 = hs.leo5_median_gbp;
  const growthPct =
    leo3 && leo5 && leo5 > leo3
      ? Math.round(((leo5 - leo3) / leo3) * 100)
      : null;
  const verified = degree.ucas_course_code_live_verified === true;

  if (!verified && !leo3 && !leo5) return null;

  const kfmt = (v) => `£${Math.round(v / 1000)}k`;

  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-1.5"
      data-testid={`${testidBase}-chipbar`}
    >
      {verified && (
        <span
          data-testid={`${testidBase}-ucas-verified`}
          title="This UCAS course code is confirmed live against UCAS' own search index"
          className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-[10px] font-underground-sc tracking-wider px-2 py-0.5"
        >
          <ShieldCheck className="h-3 w-3 text-emerald-700" aria-hidden />
          UCAS Verified
        </span>
      )}
      {leo3 && leo5 && growthPct != null && (
        <span
          data-testid={`${testidBase}-leo-trajectory`}
          title={`HESA LEO median salary: £${leo3.toLocaleString()} at 3 years → £${leo5.toLocaleString()} at 5 years post-graduation.`}
          className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-900 text-[11px] px-2 py-0.5"
        >
          <TrendingUp className="h-3 w-3 text-emerald-700" aria-hidden />
          <span className="font-medium">{kfmt(leo3)}</span>
          <span className="text-emerald-700/70" aria-hidden>→</span>
          <span className="font-medium">{kfmt(leo5)}</span>
          <span className="text-emerald-700 font-mono text-[10px] tracking-wider ml-0.5">
            +{growthPct}%
          </span>
        </span>
      )}
      {/* Degrees with only one LEO waypoint still get a compact chip
          so the trajectory row isn't jaggy across columns. */}
      {(leo5 || leo3) && !(leo3 && leo5) && (
        <span
          data-testid={`${testidBase}-leo-point`}
          className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-900 text-[11px] px-2 py-0.5"
        >
          <ShieldCheck className="h-3 w-3 text-emerald-700" aria-hidden />
          <span className="font-medium">{kfmt(leo5 || leo3)}</span>
          <span className="text-emerald-700 font-underground-sc tracking-wider text-[9px]">
            LEO · {leo5 ? "5" : "3"}yr
          </span>
        </span>
      )}
    </div>
  );
}
